/**
 * Auth-path selection helper.
 *
 * Determines whether the SPA should use the BFF session path (`session`) or
 * the existing PKCE flow (`pkce`) for the current browser.
 *
 * Decision order (highest wins):
 *   1. Kill switch: if `feature-bff-session-auth-path-kill-switch` is `true` → `pkce`
 *   2. Forced page: if the current URL pathname is `/session-signin` → `session`
 *   3. Sticky: if `bff_auth_path` key exists in localStorage → that value
 *   4. Percentage rollout: LD flag `feature-bff-session-auth-path` → `session` (truthy) / `pkce` (falsy/absent)
 *
 * The anonymous LD context key (`bff_anon_id`) is generated once per browser
 * and persisted to localStorage so assignment remains stable across reloads.
 *
 * This module deliberately does NOT import AppModule or FeatureFlagModule to
 * avoid circular dependencies — it initialises its own short-lived LDClient
 * instance using the same SDK client ID.
 */

import * as LDClient from 'launchdarkly-js-client-sdk';
import environment from '@lib/environment';
import {
  STORAGE_KEY_BFF_ANON_ID,
  STORAGE_KEY_BFF_AUTH_PATH,
} from '@constants/storage';
import { FeatureFlagKeys } from '@modules/FeatureFlagModule/featureFlagKeys';

export type AuthPath = 'pkce' | 'session';

// ---------------------------------------------------------------------------
// Anonymous browser key
// ---------------------------------------------------------------------------

/**
 * Returns the stable anonymous browser key used for the pre-auth LD context.
 * Generated once (UUID v4-like) and persisted to localStorage.
 */
export function getOrCreateAnonId(): string {
  const existing = localStorage.getItem(STORAGE_KEY_BFF_ANON_ID);
  if (existing) return existing;

  const id = crypto.randomUUID();
  localStorage.setItem(STORAGE_KEY_BFF_ANON_ID, id);
  return id;
}

// ---------------------------------------------------------------------------
// Sticky path store
// ---------------------------------------------------------------------------

/** Reads the sticky auth path from localStorage. Returns `null` if not yet assigned. */
export function readStickyPath(): AuthPath | null {
  const value = localStorage.getItem(STORAGE_KEY_BFF_AUTH_PATH);
  if (value === 'session' || value === 'pkce') return value;
  return null;
}

/** Persists the chosen auth path to localStorage for sticky assignment. */
export function writeStickyPath(path: AuthPath): void {
  localStorage.setItem(STORAGE_KEY_BFF_AUTH_PATH, path);
}

/** Removes the sticky path assignment (useful for testing). */
export function clearStickyPath(): void {
  localStorage.removeItem(STORAGE_KEY_BFF_AUTH_PATH);
}

// ---------------------------------------------------------------------------
// Pre-auth LD evaluation
// ---------------------------------------------------------------------------

/**
 * Evaluates the auth-path flag via a short-lived anonymous LD client.
 *
 * Uses the anonymous browser key as the LD context key so LD can apply
 * percentage rollout rules consistently per browser without requiring user
 * or tenant identity.
 *
 * Returns `true` if the session path is enabled, `false` for PKCE.
 * Falls back to `false` (PKCE) on any error so the app degrades safely.
 */
/**
 * Builds and initialises a short-lived anonymous LD client.
 * Shared by both flag evaluations so the context/options setup is not duplicated.
 * The caller is responsible for closing the client after use.
 */
async function initializeAnonymousLdClient(): Promise<LDClient.LDClient> {
  const ldContext: LDClient.LDContext = {
    kind: 'user',
    key: getOrCreateAnonId(),
    anonymous: true,
  };

  const ldOptions: LDClient.LDOptions = { sendEventsOnlyForVariation: true };

  if (environment.LAUNCH_DARKLY_PROXY_URL) {
    ldOptions.baseUrl = environment.LAUNCH_DARKLY_PROXY_URL;
    ldOptions.streamUrl = environment.LAUNCH_DARKLY_PROXY_URL;
    ldOptions.eventsUrl = environment.LAUNCH_DARKLY_PROXY_URL;
  }

  const client = LDClient.initialize(
    environment.LAUNCH_DARKLY_CLIENT_ID,
    ldContext,
    ldOptions,
  );
  await client.waitForInitialization(5);
  return client;
}

async function evaluateAuthPathFlagAnonymously(): Promise<boolean> {
  try {
    const client = await initializeAnonymousLdClient();
    const sessionEnabled = client.variation(
      FeatureFlagKeys.featureBffSessionAuthPath,
      false,
    ) as boolean;
    client.close();
    return sessionEnabled;
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Kill switch check
// ---------------------------------------------------------------------------

/**
 * Evaluates the kill switch flag via an anonymous LD client.
 * Returns `true` if the kill switch is engaged (force PKCE).
 * Falls back to `false` on any error so no spurious kill-switch.
 */
async function isKillSwitchEngaged(): Promise<boolean> {
  try {
    const client = await initializeAnonymousLdClient();
    const killSwitch = client.variation(
      FeatureFlagKeys.featureBffSessionAuthPathKillSwitch,
      false,
    ) as boolean;
    client.close();
    return killSwitch;
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Main: selectAuthPath
// ---------------------------------------------------------------------------

/**
 * In-flight dedup guard for selectAuthPath.
 * Concurrent callers share a single LD evaluation instead of each spinning up
 * their own anonymous LD client.
 */
let selectAuthPathInflight: Promise<AuthPath> | null = null;

/**
 * Selects the auth path for the current request/load.
 *
 * Called per-request (not once at startup) so that a kill-switch engaged
 * mid-session takes effect without a page reload.
 *
 * Concurrent callers are deduplicated — if a resolution is already in flight,
 * the same promise is returned to all callers so only one LD client is
 * initialised.
 *
 * Decision order:
 *   1. Kill switch → `pkce`
 *   2. Forced page (`/session-signin` pathname) → `session`
 *   3. Sticky localStorage value → that value
 *   4. LD percentage rollout → assign and persist the result
 */
export function selectAuthPath(): Promise<AuthPath> {
  selectAuthPathInflight ??= _selectAuthPath().finally(() => {
    selectAuthPathInflight = null;
  });
  return selectAuthPathInflight;
}

async function _selectAuthPath(): Promise<AuthPath> {
  // 1. Kill switch overrides everything including sticky and forced page.
  //    Clear any sticky 'session' assignment so the override propagates to every
  //    synchronous readStickyPath() call site (click handler, authLink,
  //    getCurrentToken, etc.) — they all fall back to PKCE. Without clearing,
  //    the stale sticky keeps those paths on 'session' even though the kill
  //    switch forced PKCE here, leaving the user unable to sign in via PKCE.
  //    Clearing (rather than writing 'pkce') means normal rollout resumes once
  //    the kill switch is disengaged.
  if (await isKillSwitchEngaged()) {
    clearStickyPath();
    return 'pkce';
  }

  // 2. Forced sign-in page forces the session path for deliberate QA testing.
  //    Persist it as sticky so the choice survives the subsequent /bff/login
  //    navigation and is honored by readStickyPath() in the click handler and
  //    every per-request path. (Without this, the forced page selected 'session'
  //    transiently but the click handler read a null sticky and fell back to PKCE.)
  if (window.location.pathname === '/session-signin') {
    writeStickyPath('session');
    return 'session';
  }

  // 3. Honor a previously assigned sticky value.
  const sticky = readStickyPath();
  if (sticky !== null) {
    return sticky;
  }

  // 4. Evaluate LD flag and persist the assignment as sticky.
  const sessionEnabled = await evaluateAuthPathFlagAnonymously();
  const chosen: AuthPath = sessionEnabled ? 'session' : 'pkce';
  writeStickyPath(chosen);
  return chosen;
}
